import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment, useState } from 'react';
import { Badge, CopyButton, InlineNotice, SectionCard, SegmentedControl, SortSelect, Table, formatCount, useTableColumns, } from '@wit-support/ui';
import { EntityLabel, ListCard, ResponsiveTableList } from '@wit-support/ui-domain';
import { ArrowRightLeft, CircleCheck } from 'lucide-react';
import { formatMinor, formatStartInZone, startSortKey } from '../format.js';
import { displayName } from '../naming.js';
import { GuaranteesList } from './guarantees-list.js';
import { planIssues, visiblePlanIssues } from '../issues.js';
import { SpendSummary, dayTotalText } from './spend-summary.js';
import { IssuesList } from './issues-list.js';
import { groupIssueText, groupTitle } from '../groups.js';
import { ColumnHeader, TableColumnsBar, adsTableColumnsKey, groupedSortValue, sortRows, useColumnSort, } from './table-kit.js';
/** Стратегия ставок коротко — и значения реестра (`BID_CAP`), и имена Meta. */
const STRATEGY_SHORT = {
    BID_CAP: 'Предельная ставка',
    COST_CAP: 'Цель по цене',
    HIGHEST_VOLUME: 'Максимум результатов',
    ROAS: 'Мин. ROAS',
    LOWEST_COST_WITH_BID_CAP: 'Предельная ставка',
    LOWEST_COST_WITHOUT_CAP: 'Максимум результатов',
    LOWEST_COST_WITH_MIN_ROAS: 'Мин. ROAS',
};
const ROAS_STRATEGIES = new Set(['ROAS', 'LOWEST_COST_WITH_MIN_ROAS']);
/** «1,25» — порог ROAS числом, как его вводили в шаблоне. */
function roasText(v) {
    return String(Math.round(v * 10_000) / 10_000).replace('.', ',');
}
/**
 * Стратегия плана: «Предельная ставка $25.00», «Мин. ROAS 1,25». Порог ROAS
 * — из плана (`roasFloor`, новый auto-ads), иначе — значение формы
 * (`roasFallback`: шаблон + изменения запуска).
 */
export function strategyText(l, roasFallback) {
    if (l.bidStrategy == null)
        return 'не выбрана';
    const name = STRATEGY_SHORT[l.bidStrategy] ?? l.bidStrategy;
    if (ROAS_STRATEGIES.has(l.bidStrategy)) {
        const roas = l.roasFloor ?? roasFallback ?? null;
        return roas != null ? `${name} ${roasText(roas)}` : name;
    }
    // разные ставки групп объявлений (лесенка) — диапазоном сервера (v4 §11.3)
    if (l.bidRange != null) {
        return `${name} ${formatMinor(l.bidRange.min, l.currency)}–${formatMinor(l.bidRange.max, l.currency)}`;
    }
    return l.bidAmountMinor != null ? `${name} ${formatMinor(l.bidAmountMinor, l.currency)}` : name;
}
/**
 * «Ставки» сводки «Создать?» (v4 §11.3): стратегия и диапазон ставок плана,
 * когда ставки разные (лесенка копий по кампаниям или внутри кампании); одна
 * ставка на весь план — null (строки нет).
 */
export function planBidsText(preview) {
    // по валюте отдельно: ставки разных валют не сравниваются (ревью SB-4 P2-2)
    const byCurrency = new Map();
    const strategies = new Set();
    for (const l of preview.launches) {
        if (l.bidStrategy != null)
            strategies.add(l.bidStrategy);
        const bids = byCurrency.get(l.currency) ?? [];
        if (l.bidRange != null)
            bids.push(l.bidRange.min, l.bidRange.max);
        else if (l.bidAmountMinor != null)
            bids.push(l.bidAmountMinor);
        byCurrency.set(l.currency, bids);
    }
    const [strategy] = [...strategies];
    if (strategies.size !== 1 || strategy == null)
        return null;
    const ranges = [...byCurrency.entries()].filter(([, b]) => new Set(b).size >= 2);
    if (ranges.length === 0)
        return null;
    const name = STRATEGY_SHORT[strategy] ?? strategy;
    return ranges
        .map(([cur, b]) => `${name} ${formatMinor(Math.min(...b), cur)}–${formatMinor(Math.max(...b), cur)}`)
        .join('; ');
}
/** Креатив словами: подпись auto-ads, экрана или по id. */
export function creativeText(c, labels = {}) {
    const own = labels.creativeLabelOf?.(c) ?? null;
    if (c.label != null && c.label !== '')
        return c.label;
    if (own != null)
        return own;
    if (c.kind === 'post')
        return `пост ${c.objectStoryId ?? '—'}`;
    const kind = c.kind === 'video' ? 'видео' : 'картинка';
    return c.name != null && c.name !== ''
        ? `${kind} «${c.name}»`
        : `${kind} ${c.assetId?.slice(0, 8) ?? ''}`.trim();
}
/** Название поста-креатива (подпись auto-ads или экрана); null — «Пост» + id. */
function postCreativeName(c, labels) {
    if (c.label != null && c.label !== '')
        return c.label;
    return labels.creativeLabelOf?.(c) ?? null;
}
/**
 * Разные креативы запуска по порядку: список auto-ads (`creatives`, новый
 * auto-ads), иначе — собранный из объявлений всех групп.
 */
export function launchCreatives(l) {
    if (l.creatives != null && l.creatives.length > 0)
        return l.creatives;
    const seen = new Set();
    const out = [];
    for (const as of l.adSets) {
        for (const ad of as.ads) {
            const key = `${ad.creative.kind}:${ad.creative.objectStoryId ?? ad.creative.assetId ?? ''}`;
            if (seen.has(key))
                continue;
            seen.add(key);
            out.push(ad.creative);
        }
    }
    return out;
}
/** «Креатив: …» под именем кампании — одинаковые имена кампаний различаются креативом. */
function Creatives({ l, labels }) {
    const list = launchCreatives(l);
    if (list.length === 0)
        return null;
    return (_jsxs("div", { className: "mt-1 text-xs font-normal text-[var(--color-text-secondary)] [overflow-wrap:anywhere]", "data-testid": `preview-creatives-${l.index}`, children: [_jsx("span", { className: "text-[var(--color-text-muted)]", children: list.length === 1 ? 'Креатив:' : 'Креативы:' }), ' ', list.map((c, i) => (_jsxs(Fragment, { children: [i > 0 ? ' · ' : null, c.kind === 'post' && c.objectStoryId ? (_jsx(EntityLabel, { kind: "post", id: c.objectStoryId, name: postCreativeName(c, labels), variant: "inline", avatar: false })) : (creativeText(c, labels))] }, `${c.kind}:${c.objectStoryId ?? c.assetId ?? i}`)))] }));
}
/**
 * Старт запуска — по Лос-Анджелесу, тем же форматом, что в пачке и на
 * карточке запуска: «14.10 03:00 Лос-Анджелес» (`formatStartInZone`).
 */
export function startText(l) {
    if (l.startTime == null && !l.startTimeLocal)
        return 'Сразу после проверки';
    return formatStartInZone(l) ?? 'Сразу после проверки';
}
const PLAN_COLUMNS = [
    { id: 'campaign', label: 'Кампания', hideable: false, defaultPin: 'left' },
    { id: 'account', label: 'Кабинет' },
    { id: 'budget', label: 'Бюджет в день' },
    { id: 'strategy', label: 'Стратегия' },
    { id: 'start', label: 'Старт' },
    { id: 'adsets', label: 'Группы и объявления' },
];
const PLAN_HINTS = {
    strategy: 'Как Meta назначает ставку: правило и его порог (ставка или окупаемость).',
};
const PLAN_DESC_FIRST = new Set(['budget', 'adsets']);
/** Ключ «Сразу после проверки»: меньше любого настенного времени. */
const IMMEDIATE_START = Number.MIN_SAFE_INTEGER;
export function planSortValue(l, key, labels) {
    switch (key) {
        case 'campaign':
            return displayName(l.campaignName) ?? null;
        case 'account':
            return l.accountName;
        case 'budget':
            // суммы разных валют несравнимы: сначала валюта, внутри неё сумма
            return groupedSortValue(l.currency, l.dailyBudgetMinor);
        case 'strategy':
            return strategyText(l, labels.roasFallback);
        case 'start':
            // как в ячейке (`startText`): «Сразу после проверки» — раньше любого времени
            return formatStartInZone(l) == null ? IMMEDIATE_START : startSortKey(l);
        case 'adsets':
            return l.adSets.reduce((n, as) => n + as.ads.length, 0);
    }
}
function PlanCell({ column: c, l, labels, }) {
    switch (c.id) {
        case 'campaign':
            return (_jsxs(Table.Cell, { column: c, className: "font-medium", children: [_jsx(CampaignName, { l: l }), _jsx(Creatives, { l: l, labels: labels }), _jsx(Landings, { l: l, labels: labels })] }));
        case 'account':
            return _jsx(Table.Cell, { column: c, children: l.accountName });
        case 'budget':
            return (_jsx(Table.Cell, { column: c, className: "text-right", children: _jsx(Budget, { l: l }) }));
        case 'strategy':
            return _jsx(Table.Cell, { column: c, children: strategyText(l, labels.roasFallback) });
        case 'start':
            return _jsx(Table.Cell, { column: c, children: startText(l) });
        case 'adsets':
            return (_jsx(Table.Cell, { column: c, children: _jsx(AdSetsList, { l: l, labels: labels }) }));
    }
}
/**
 * Запуски плана: таблица на ПК (колонки настраиваются, сортировка по любой
 * колонке — весь план на экране) и карточки на телефоне (как раньше).
 */
function PlanTable({ launches, labels, }) {
    const cols = useTableColumns(adsTableColumnsKey('plan'), PLAN_COLUMNS);
    const [sort, setSort] = useColumnSort(cols);
    const items = sortRows(launches, sort, (l, key) => planSortValue(l, key, labels));
    return (_jsxs("div", { className: "flex flex-col gap-2", children: [_jsx(TableColumnsBar, { columns: cols, sort: sort, className: "hidden md:flex" }), _jsx(ResponsiveTableList, { items: items, getKey: (l) => l.index, headerCells: cols.visible.map((c) => (_jsx(ColumnHeader, { column: c, sort: sort, onSortChange: setSort, align: c.id === 'budget' ? 'right' : 'left', defaultDirection: PLAN_DESC_FIRST.has(c.id) ? 'desc' : 'asc', ...(PLAN_HINTS[c.id] ? { hint: PLAN_HINTS[c.id] } : {}) }, c.id))), renderRow: (l) => (_jsx(Table.Row, { "data-testid": `preview-launch-${l.index}`, children: cols.visible.map((c) => (_jsx(PlanCell, { column: c, l: l, labels: labels }, c.id))) }, l.index)), renderMobileCard: (l) => _jsx(PlanCard, { l: l, labels: labels }) })] }));
}
function CampaignName({ l }) {
    return (_jsxs(_Fragment, { children: [displayName(l.campaignName), l.copyIndex > 0 ? _jsxs(Badge, { className: "ml-1", children: ["\u043A\u043E\u043F\u0438\u044F ", l.copyIndex + 1] }) : null] }));
}
/** Неизвестная ссылка — так и сказать, а не молчать. */
export const UNKNOWN_LANDING_TEXT = 'ссылка неизвестна';
/**
 * «Куда ведёт: <ссылка с UTM>» — одна строка на каждую разную ссылку запуска.
 * Ссылки — из плана auto-ads (`landings`, ровно то, что уходит в Meta); null
 * в списке — ссылка неизвестна. Старый auto-ads без поля — строки нет.
 */
function Landings({ l, labels }) {
    const landings = l.landings ?? [];
    if (landings.length === 0)
        return null;
    return (_jsx("ul", { className: "mt-1 flex flex-col gap-0.5 text-xs font-normal text-[var(--color-text-secondary)]", "data-testid": `preview-landing-${l.index}`, children: landings.map((url) => {
            const view = url != null && labels.analyticsMarks ? landingView(url) : null;
            return (_jsxs("li", { className: "flex flex-wrap items-center gap-x-1 [overflow-wrap:anywhere]", children: [_jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u041A\u0443\u0434\u0430 \u0432\u0435\u0434\u0451\u0442:" }), ' ', url == null || view == null ? (url != null ? (_jsx("span", { className: "font-mono", children: url })) : (UNKNOWN_LANDING_TEXT)) : (_jsxs(_Fragment, { children: [_jsx("span", { className: "font-mono", title: url, children: view.short }), view.analytics ? _jsx("span", { title: url, children: "+ \u043C\u0435\u0442\u043A\u0438 \u0430\u043D\u0430\u043B\u0438\u0442\u0438\u043A\u0438 \u2713" }) : null, _jsx(CopyButton, { value: url, size: "sm", variant: "ghost", ariaLabel: "\u041A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u0442\u044C \u043F\u043E\u043B\u043D\u0443\u044E \u0441\u0441\u044B\u043B\u043A\u0443", successMessage: "\u041F\u043E\u043B\u043D\u0430\u044F \u0441\u0441\u044B\u043B\u043A\u0430 \u0441\u043A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u043D\u0430", children: "\u043A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u0442\u044C \u043F\u043E\u043B\u043D\u0443\u044E \u0441\u0441\u044B\u043B\u043A\u0443" })] }))] }, url ?? ''));
        }) }));
}
/**
 * Ссылка плана коротко (v4 §11.3): без `https://` и без меток `utm_*` — вместо
 * них «+ метки аналитики ✓»; полная ссылка — в подсказке и по «копировать».
 * Свои параметры (`?lg=en`) остаются видны. Не разобралась — null (как есть).
 */
/** Метки-номера Meta, которые дописывает конструктор (v4 §6.1). */
const ID_TAG_KEYS = new Set(['utm_medium', 'utm_campaign', 'utm_content']);
export function landingView(url) {
    let u;
    try {
        u = new URL(url);
    }
    catch {
        return null;
    }
    // прячутся только метки-номера Meta ({{…}}); явные значения человека остаются видны
    const macro = [...u.searchParams.entries()].filter(([k, v]) => ID_TAG_KEYS.has(k.toLowerCase()) && /^\{\{.+\}\}$/.test(v));
    const analytics = macro.some(([k]) => k.toLowerCase() === 'utm_medium');
    for (const [k] of macro)
        u.searchParams.delete(k);
    // URLSearchParams кодирует фигурные скобки и т. п. — показываем как было
    const query = decodeURIComponent(u.searchParams.toString());
    const path = u.pathname === '/' ? '' : u.pathname;
    return { short: `${u.host}${path}${query ? `?${query}` : ''}${u.hash}`, analytics };
}
function Budget({ l }) {
    // v4 §3.11: бюджет до уникальных центов — сервер (`baseDailyBudgetMinor`)
    // план — бюджет ДО уникальных центов, сумма с центами — мелко (план SB-4); старый — как раньше
    const base = l.baseDailyBudgetMinor;
    const withCents = base != null && base !== l.dailyBudgetMinor;
    return (_jsxs(_Fragment, { children: [_jsx("span", { className: "tabular-nums", children: formatMinor(withCents ? base : l.dailyBudgetMinor, l.currency) }), _jsx("span", { className: "ml-1 text-xs text-[var(--color-text-muted)]", children: l.budgetLevel }), withCents ? (_jsxs("span", { className: "block text-xs text-[var(--color-text-muted)] tabular-nums", "data-testid": `preview-budget-cents-${l.index}`, children: ["\u0441 \u0443\u043D\u0438\u043A\u0430\u043B\u044C\u043D\u044B\u043C\u0438 \u0446\u0435\u043D\u0442\u0430\u043C\u0438 ", formatMinor(l.dailyBudgetMinor, l.currency)] })) : null] }));
}
/**
 * React-ключ строки группы объявлений плана (дефект 3, спека v4 §11.9): по
 * `localRef` нового auto-ads — он уникален в запуске; имена групп могут
 * совпадать (копии, одинаковые креативы), и ключ по имени склеивал строки.
 * Старый auto-ads без `localRef` — по позиции в списке.
 */
export function adSetRowKey(as, index) {
    const ref = as.localRef;
    return typeof ref === 'string' && ref.length > 0 ? `ref:${ref}` : `i:${index}`;
}
export function AdSetsList({ l, labels }) {
    if (l.adSets.some((as) => as.group != null))
        return _jsx(GroupAdSetsList, { l: l, labels: labels });
    return (_jsx("ul", { className: "flex flex-col gap-0.5 text-xs", children: l.adSets.map((as, i) => (_jsxs("li", { children: [_jsx("span", { className: "font-medium", children: displayName(as.name) }), l.bidRange != null && as.bidAmountMinor != null
                    ? ` · ${as.copyIndex != null ? `копия ${as.copyIndex + 1} · ` : ''}ставка ${formatMinor(as.bidAmountMinor, l.currency)}`
                    : '', as.dailyBudgetMinor != null ? ` · ${formatMinor(as.dailyBudgetMinor, l.currency)}` : '', ' · ', formatCount(as.ads.length, ['объявление', 'объявления', 'объявлений']), as.targeting.length > 0 ? (_jsxs("span", { className: "text-[var(--color-text-muted)]", children: [' ', "(", as.targeting
                            .map((t) => `${t.label}: ${labels.displayOf?.(t) ?? t.display}`)
                            .join('; '), ")"] })) : null] }, adSetRowKey(as, i)))) }));
}
/**
 * Таргетинг, общий для всех групп объявлений кампании: «Гео: Мир · Возраст:
 * 25–65 · Пол: Женщины» — каждое значение с подписью поля (E-2 P3: голые
 * «выкл. · … · нет» не читались).
 */
export function commonTargetingText(common, labels) {
    return common.map((t) => `${t.label}: ${labels.displayOf?.(t) ?? t.display}`).join(' · ');
}
/**
 * Подпись группы объявлений плана: «① LAL 1 %»; если в кампании несколько групп
 * объявлений одной группы шаблона и копии (по креативу на каждую) — с креативом:
 * «① LAL 1 % · Пост …000001» (E-2 P3-5: три одинаковые «① LAL 1 %» не различить).
 */
export function planAdSetTitle(as, l, labels = {}) {
    if (!as.group)
        return displayName(as.name) ?? as.name;
    const head = groupTitle(as.group.index, as.group.label);
    const twins = l.adSets.filter((x) => x.group?.key === as.group.key && (x.copyIndex ?? 0) === (as.copyIndex ?? 0));
    if (twins.length < 2)
        return head;
    const c = as.ads[0]?.creative;
    const creative = c ? (labels.creativeLabelOf?.(c) ?? c.label ?? null) : null;
    return creative ? `${head} · ${creative}` : `${head} · креатив ${twins.indexOf(as) + 1}`;
}
/** «A+ выкл» / «A+ вкл»; неизвестно (старый auto-ads) — пусто. */
function advantageShort(v) {
    return v == null ? null : v ? 'A+ вкл' : 'A+ выкл';
}
/** Отличия группы шаблона словами (без Advantage+ — он своей частью). */
function differencesText(as, labels) {
    const list = (as.differences ?? []).filter((d) => d.fieldKey !== 'adset.advantageAudience' && d.fieldKey !== 'adset.bidAmountMinor');
    return list.length > 0
        ? list.map((d) => `${d.label}: ${labels.displayOf?.(d) ?? d.display}`).join(' · ')
        : null;
}
/**
 * Группы объявлений кампании с группами шаблона (v4 §11.3, «По кампаниям»):
 * «1-2-1 · 2 группы объявлений», по строке на группу объявлений «① LAL23 ·
 * Lookalike: … · A+ выкл · $21.69 · 1 объявл.», затем общее — таргетинг,
 * одинаковый у всех групп объявлений.
 */
function GroupAdSetsList({ l, labels }) {
    const common = l.adSets[0]?.targeting.filter((t) => l.adSets.every((as) => as.targeting.some((x) => x.fieldKey === t.fieldKey && x.display === t.display)));
    const copies = new Set(l.adSets.map((as) => as.copyIndex ?? 0)).size > 1;
    return (_jsxs("div", { className: "flex flex-col gap-0.5 text-xs", "data-testid": `plan-groups-${l.index}`, children: [_jsxs("span", { className: "font-medium", children: [l.structure ? `${l.structure.shape} · ` : '', formatCount(l.adSets.length, [
                        'группа объявлений',
                        'группы объявлений',
                        'групп объявлений',
                    ])] }), _jsx("ul", { className: "flex flex-col gap-0.5", children: l.adSets.map((as, i) => {
                    const parts = [
                        planAdSetTitle(as, l, labels),
                        copies && as.copyIndex != null ? `копия ${as.copyIndex + 1}` : null,
                        differencesText(as, labels),
                        advantageShort(as.advantageAudience),
                        as.bidAmountMinor != null ? formatMinor(as.bidAmountMinor, l.currency) : null,
                        as.dailyBudgetMinor != null
                            ? `${formatMinor(as.dailyBudgetMinor, l.currency)}/день`
                            : null,
                        formatCount(as.ads.length, ['объявл.', 'объявл.', 'объявл.']),
                    ].filter((x) => x != null && x !== '');
                    return (_jsx("li", { className: "[overflow-wrap:anywhere]", children: parts.join(' · ') }, adSetRowKey(as, i)));
                }) }), common && common.length > 0 ? (_jsxs("span", { className: "text-[var(--color-text-muted)]", children: ["\u041E\u0431\u0449\u0435\u0435: ", commonTargetingText(common, labels)] })) : null] }));
}
const GROUP_COLUMNS = [
    { id: 'account', label: 'Кабинет' },
    { id: 'campaign', label: 'Кампания' },
    { id: 'group', label: 'Группа', hideable: false, defaultPin: 'left' },
    { id: 'copy', label: 'Копия' },
    { id: 'differences', label: 'Отличия' },
    { id: 'advantage', label: 'Advantage+' },
    { id: 'bid', label: 'Ставка' },
    { id: 'budget', label: 'В день (ABO)' },
    { id: 'ads', label: 'Объявлений' },
];
export function groupSortValue(r, key, labels) {
    switch (key) {
        case 'account':
            return r.l.accountName;
        case 'campaign':
            return displayName(r.l.campaignName) ?? null;
        case 'group':
            return r.as.group ? r.as.group.index : null;
        case 'copy':
            return r.as.copyIndex ?? null;
        case 'differences':
            return differencesText(r.as, labels);
        case 'advantage':
            return r.as.advantageAudience == null ? null : r.as.advantageAudience ? 1 : 0;
        case 'bid':
            return r.as.bidAmountMinor == null
                ? null
                : groupedSortValue(r.l.currency, r.as.bidAmountMinor);
        case 'budget':
            return r.as.dailyBudgetMinor == null
                ? null
                : groupedSortValue(r.l.currency, r.as.dailyBudgetMinor);
        case 'ads':
            return r.as.ads.length;
    }
}
function GroupCell({ column: c, r, labels, }) {
    const { l, as } = r;
    switch (c.id) {
        case 'account':
            return _jsx(Table.Cell, { column: c, children: l.accountName });
        case 'campaign':
            return _jsx(Table.Cell, { column: c, children: displayName(l.campaignName) });
        case 'group':
            return (_jsx(Table.Cell, { column: c, className: "font-medium", children: planAdSetTitle(as, l, labels) }));
        case 'copy':
            return _jsx(Table.Cell, { column: c, children: as.copyIndex != null ? as.copyIndex + 1 : '—' });
        case 'differences':
            return _jsx(Table.Cell, { column: c, children: differencesText(as, labels) ?? 'как общее' });
        case 'advantage':
            return (_jsx(Table.Cell, { column: c, children: as.advantageAudience == null ? '—' : as.advantageAudience ? 'вкл' : 'выкл' }));
        case 'bid':
            return (_jsx(Table.Cell, { column: c, className: "text-right tabular-nums", children: as.bidAmountMinor != null ? formatMinor(as.bidAmountMinor, l.currency) : '—' }));
        case 'budget':
            return (_jsx(Table.Cell, { column: c, className: "text-right tabular-nums", children: as.dailyBudgetMinor != null ? formatMinor(as.dailyBudgetMinor, l.currency) : '—' }));
        case 'ads':
            return _jsx(Table.Cell, { column: c, children: as.ads.length });
    }
}
/**
 * «По группам» (v4 §11.3, UA В8): строка = группа объявлений; «Кабинет» и
 * «Копия» скрыты, если значение одно. Сортировка по любой колонке; на узком
 * экране — карточки с «Сортировать по».
 */
function GroupsPlanTable({ launches, labels, }) {
    const rows = launches.flatMap((l) => l.adSets.map((as, i) => ({ l, as, i })));
    const oneAccount = new Set(launches.map((l) => l.platformAccountId)).size <= 1;
    const oneCopy = new Set(rows.map((r) => r.as.copyIndex ?? 0)).size <= 1;
    const abo = rows.some((r) => r.as.dailyBudgetMinor != null);
    const defs = GROUP_COLUMNS.filter((c) => !(c.id === 'account' && oneAccount) &&
        !(c.id === 'copy' && oneCopy) &&
        !(c.id === 'budget' && !abo));
    const cols = useTableColumns(adsTableColumnsKey('plan-groups'), defs);
    const [sort, setSort] = useColumnSort(cols);
    const items = sortRows(rows, sort, (r, key) => groupSortValue(r, key, labels));
    return (_jsxs("div", { className: "flex flex-col gap-2", "data-testid": "plan-by-groups", children: [_jsx(TableColumnsBar, { columns: cols, sort: sort, className: "hidden md:flex" }), _jsx(SortSelect, { id: "plan-groups-sort", label: "\u0421\u043E\u0440\u0442\u0438\u0440\u043E\u0432\u0430\u0442\u044C \u043F\u043E", sort: sort, onSortChange: setSort, options: cols.visible.map((c) => ({
                    key: c.id,
                    label: c.def.label,
                    defaultDirection: c.id === 'bid' || c.id === 'budget' || c.id === 'ads'
                        ? 'desc'
                        : 'asc',
                })) }), _jsx(ResponsiveTableList, { items: items, getKey: (r) => `${r.l.index}:${adSetRowKey(r.as, r.i)}`, headerCells: cols.visible.map((c) => (_jsx(ColumnHeader, { column: c, sort: sort, onSortChange: setSort, align: c.id === 'bid' || c.id === 'budget' ? 'right' : 'left', defaultDirection: c.id === 'bid' || c.id === 'budget' ? 'desc' : 'asc' }, c.id))), renderRow: (r) => (_jsx(Table.Row, { children: cols.visible.map((c) => (_jsx(GroupCell, { column: c, r: r, labels: labels }, c.id))) }, `${r.l.index}:${adSetRowKey(r.as, r.i)}`)), renderMobileCard: (r) => (_jsxs(ListCard, { className: "text-sm", children: [_jsxs("span", { className: "font-medium [overflow-wrap:anywhere]", children: [planAdSetTitle(r.as, r.l, labels), !oneCopy && r.as.copyIndex != null ? ` · копия ${r.as.copyIndex + 1}` : '', " \u00B7", ' ', displayName(r.l.campaignName)] }), _jsx("span", { className: "text-xs [overflow-wrap:anywhere]", children: [differencesText(r.as, labels), advantageShort(r.as.advantageAudience)]
                                .filter(Boolean)
                                .join(' · ') }), _jsx("span", { className: "text-xs tabular-nums", children: [
                                r.as.bidAmountMinor != null ? formatMinor(r.as.bidAmountMinor, r.l.currency) : null,
                                r.as.dailyBudgetMinor != null
                                    ? `${formatMinor(r.as.dailyBudgetMinor, r.l.currency)}/день`
                                    : null,
                                formatCount(r.as.ads.length, ['объявл.', 'объявл.', 'объявл.']),
                            ]
                                .filter(Boolean)
                                .join(' · ') })] })) })] }));
}
const PLAN_VIEW_KEY = 'ads-launcher:plan-view';
function readPlanView() {
    try {
        return globalThis.localStorage?.getItem(PLAN_VIEW_KEY) === 'groups' ? 'groups' : 'campaigns';
    }
    catch {
        return 'campaigns';
    }
}
/** Переключатель «По кампаниям | По группам» запоминается (`localStorage`, в try/catch). */
function usePlanView() {
    const [view, setView] = useState(readPlanView);
    return [
        view,
        (v) => {
            setView(v);
            try {
                globalThis.localStorage?.setItem(PLAN_VIEW_KEY, v);
            }
            catch {
                // хранилище недоступно — выбор живёт до перезагрузки
            }
        },
    ];
}
/** Карточка запуска плана на узком экране — те же данные, что строка таблицы. */
function PlanCard({ l, labels }) {
    return (_jsxs(ListCard, { className: "text-sm", "data-testid": `preview-launch-card-${l.index}`, children: [_jsxs("div", { className: "flex min-w-0 flex-col gap-0.5", children: [_jsx("span", { className: "font-medium [overflow-wrap:anywhere]", children: _jsx(CampaignName, { l: l }) }), _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: l.accountName }), _jsx(Creatives, { l: l, labels: labels }), _jsx(Landings, { l: l, labels: labels })] }), _jsxs("dl", { className: "grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm", children: [_jsx("dt", { className: "text-[var(--color-text-muted)]", children: "\u0411\u044E\u0434\u0436\u0435\u0442" }), _jsx("dd", { children: _jsx(Budget, { l: l }) }), _jsx("dt", { className: "text-[var(--color-text-muted)]", children: "\u0421\u0442\u0440\u0430\u0442\u0435\u0433\u0438\u044F" }), _jsx("dd", { children: strategyText(l, labels.roasFallback) }), _jsx("dt", { className: "text-[var(--color-text-muted)]", children: "\u0421\u0442\u0430\u0440\u0442" }), _jsx("dd", { children: startText(l) })] }), _jsx(AdSetsList, { l: l, labels: labels })] }));
}
/**
 * Файлы, которые запуски перенесут в свои кабинеты перед созданием
 * креативов («Файл «X» будет перенесён в кабинет «Y»»). Ничего делать не
 * нужно — это сообщение, а не проблема.
 */
export function MediaTransfers({ preview }) {
    const list = preview.mediaTransfers;
    if (list.length === 0)
        return null;
    return (_jsx(InlineNotice, { variant: "info", icon: ArrowRightLeft, as: "div", "data-testid": "media-transfers", children: _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "font-medium", children: "\u0424\u0430\u0439\u043B\u044B \u043F\u0435\u0440\u0435\u043D\u0435\u0441\u0443\u0442\u0441\u044F \u0432 \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u044B \u0441\u0430\u043C\u0438" }), _jsx("ul", { className: "flex flex-col gap-0.5", children: list.map((t) => (_jsx("li", { children: t.message ||
                            `Файл «${t.name ?? t.assetId.slice(0, 8)}» будет перенесён в кабинет «${t.accountName}»` }, `${t.platformAccountId}:${t.assetId}`))) })] }) }));
}
/**
 * План пачки после «Проверить»: будущие кампании (имя, куда ведёт, кабинет,
 * бюджет с центами, стратегия и ставка, старт по Лос-Анджелесу, группы и объявления),
 * ошибки (блокируют создание), предупреждения и чек-лист «Гарантии».
 */
export function PreviewPanel({ preview, labelOf, guaranteeDescriptors, stale, labels = {}, }) {
    // группы шаблона плана — для подписи замечаний «① LAL23» вместо «Группа шаблона «LAL23»»
    const planGroups = [
        ...new Map(preview.launches
            .flatMap((l) => l.adSets.map((as) => as.group))
            .filter((g) => g != null)
            .sort((a, b) => a.index - b.index)
            .map((g) => [g.key, g])).values(),
    ];
    const groupLabels = [];
    for (const g of planGroups)
        groupLabels[g.index] = { key: g.key, label: g.label };
    const allIssues = visiblePlanIssues(planIssues(preview)).map((i) => planGroups.length > 0 ? { ...i, message: groupIssueText(i, denseGroups(groupLabels)) } : i);
    const [view, setView] = usePlanView();
    const hasGroups = planGroups.length > 0;
    const manyAdSets = preview.launches.some((l) => l.adSets.length > 1);
    const launchLabel = (index) => {
        const l = preview.launches.find((x) => x.index === index);
        return l ? `${displayName(l.campaignName)} (${l.accountName})` : `запуск ${index + 1}`;
    };
    const accounts = new Set(preview.launches.map((l) => l.platformAccountId)).size;
    return (_jsx(SectionCard, { title: "\u041F\u043B\u0430\u043D", hint: _jsxs("span", { className: "text-sm text-[var(--color-text-secondary)]", children: [formatCount(preview.totals.campaigns, ['кампания', 'кампании', 'кампаний']), " \u0432", ' ', formatCount(accounts, ['кабинете', 'кабинетах', 'кабинетах']), " \u00B7", ' ', formatCount(preview.totals.adSets, [
                    'группа объявлений',
                    'группы объявлений',
                    'групп объявлений',
                ]), ' ', "\u00B7 ", formatCount(preview.totals.ads, ['объявление', 'объявления', 'объявлений']), " \u00B7 \u0431\u044E\u0434\u0436\u0435\u0442 \u0432 \u0434\u0435\u043D\u044C ", dayTotalText(preview.spend, preview.totals.dailyBudgetMinorByCurrency)] }), children: _jsxs("div", { className: "flex min-w-0 flex-col gap-4 overflow-x-auto", "data-testid": "preview-panel", children: [stale ? (_jsx(InlineNotice, { variant: "warning", bordered: true, as: "div", children: "\u041D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 \u0438\u0437\u043C\u0435\u043D\u0438\u043B\u0438\u0441\u044C \u043F\u043E\u0441\u043B\u0435 \u043F\u0440\u043E\u0432\u0435\u0440\u043A\u0438 \u2014 \u043D\u0430\u0436\u043C\u0438\u0442\u0435 \u00AB\u041F\u0440\u043E\u0432\u0435\u0440\u0438\u0442\u044C\u00BB \u0435\u0449\u0451 \u0440\u0430\u0437." })) : null, preview.spend != null ? (
                // итог в день крупно — по деньгам сервера (v4 §11.3); старый auto-ads — прежняя строка в шапке
                _jsx(SpendSummary, { spend: preview.spend })) : null, _jsx(IssuesList, { issues: allIssues, labelOf: labelOf, launchLabel: launchLabel }), _jsx(MediaTransfers, { preview: preview }), preview.canCreate && allIssues.every((i) => i.severity === 'info') ? (_jsx(InlineNotice, { variant: "info", icon: CircleCheck, as: "div", children: "\u041E\u0448\u0438\u0431\u043E\u043A \u043D\u0435\u0442 \u2014 \u043C\u043E\u0436\u043D\u043E \u0441\u043E\u0437\u0434\u0430\u0432\u0430\u0442\u044C." })) : null, hasGroups || manyAdSets ? (_jsx(SegmentedControl, { label: "\u041A\u0430\u043A \u043F\u043E\u043A\u0430\u0437\u0430\u0442\u044C \u043F\u043B\u0430\u043D", size: "sm", value: view, onChange: setView, options: [
                        { value: 'campaigns', label: 'По кампаниям' },
                        { value: 'groups', label: 'По группам' },
                    ] })) : null, (hasGroups || manyAdSets) && view === 'groups' ? (_jsx(GroupsPlanTable, { launches: preview.launches, labels: { ...labels, analyticsMarks: preview.spend != null } })) : (_jsx(PlanTable, { launches: preview.launches, labels: { ...labels, analyticsMarks: preview.spend != null } })), _jsxs("div", { className: "flex flex-col gap-2", children: [_jsx("span", { className: "text-sm font-medium", children: "\u0413\u0430\u0440\u0430\u043D\u0442\u0438\u0438" }), _jsx(GuaranteesList, { rows: preview.guarantees, descriptors: guaranteeDescriptors })] })] }) }));
}
/** Группы плана по номеру: пропуски (снятые при запуске) — заглушкой, чтобы номер «①» совпадал с шаблоном. */
function denseGroups(byIndex) {
    return Array.from(byIndex, (g, i) => g ?? { key: `#${i}`, label: '' });
}
//# sourceMappingURL=preview-panel.js.map